import React from "react";
import DOMPurify from "dompurify";
import { Grid } from "@mui/material";

const CaseStudies = ({ projects, primaryColor }) => {
  const caseStudies = projects || [];
  
  // Use same mobile check as in OurTechnology.js
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;
  // Helper to check if a URL is a YouTube or Vimeo link
  const getEmbedTypeAndUrl = (url) => {
    if (!url) return { type: null, embedUrl: null };
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
    if (ytMatch && ytMatch[1]) {
      return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}` };
    }
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch && vimeoMatch[1]) {
      return { type: 'vimeo', embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
    }
    return { type: 'video', embedUrl: url };
  };

  return (
    <Grid container style={{ padding: isMobile ? '60px 30px' : '60px 60px' }}>
      <Grid size={12}>
        <h3 style={{ margin: 0, marginBottom: 20, fontSize: "26px", color: "#000" }}>
          Case Studies
        </h3>
      </Grid>
      <Grid size={12} style={{ marginTop: "20px" }}>
        <Grid container spacing={4}>
          {caseStudies.map((study, index) => (
            <React.Fragment key={study.projectId}>
              <Grid size={12}>
                {study.type === 'Video' ? (
                  // Use the same layout as images: media left, text right (desktop), stacked (mobile)
                  isMobile ? (
                    <Grid container style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                      <Grid item xs={12} style={{ paddingLeft: 0, paddingBottom: 16 }}>
                        <h3 style={{ fontSize: "20px", marginBottom: "10px", marginTop: "10px" }}>
                          {study.category}
                        </h3>
                        <p>
                          <i className="fa fa-map-marker" style={{ marginRight: "8px", color: "#229626" }}></i>
                          {study.county}
                        </p>
                        <div>
                          {study.description
                            ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(study.description) }} />
                            : null}
                        </div>
                      </Grid>
                      <Grid item xs={12} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {(() => {
                          const { type, embedUrl } = getEmbedTypeAndUrl(study.image);
                          return (
                            <div style={{
                              position: 'relative',
                              width: '100%',
                              paddingTop: '56.25%', // 16:9 aspect ratio
                              marginBottom: '30px',
                              borderRadius: '2px',
                              overflow: 'hidden',
                              background: '#000'
                            }}>
                              {type === 'youtube' || type === 'vimeo' ? (
                                <iframe
                                  src={embedUrl}
                                  title={study.description || (type === 'youtube' ? 'YouTube video' : 'Vimeo video')}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0
                                  }}
                                />
                              ) : (
                                <video
                                  src={embedUrl}
                                  controls
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '2px',
                                    border: 0
                                  }}
                                >
                                  Your browser does not support the video tag.
                                </video>
                              )}
                            </div>
                          );
                        })()}
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container style={{ display: "flex", overflow: "hidden", flexWrap: 'nowrap' }}>
                      <Grid item size={{ xs: 12, md: 6 }} style={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                        {(() => {
                          const { type, embedUrl } = getEmbedTypeAndUrl(study.image);
                          // Use 533px width for video, 300px for image
                          const mediaWidth = (study.type === 'Video') ? 500 : 300;
                          return (
                            <div style={{
                              position: 'relative',
                              width: mediaWidth,
                              maxWidth: mediaWidth,
                              height: 0,
                              paddingTop: '56.25%', // 16:9 aspect ratio
                              borderRadius: '2px',
                              overflow: 'hidden',
                              background: '#000',
                              marginRight: 20
                            }}>
                              {type === 'youtube' || type === 'vimeo' ? (
                                <iframe
                                  src={embedUrl}
                                  title={study.description || (type === 'youtube' ? 'YouTube video' : 'Vimeo video')}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0
                                  }}
                                />
                              ) : (
                                <video
                                  src={embedUrl}
                                  controls
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '2px',
                                    border: 0
                                  }}
                                >
                                  Your browser does not support the video tag.
                                </video>
                              )}
                            </div>
                          );
                        })()}
                      </Grid>
                      <Grid item size={{ xs: 12, md: 8 }} style={{ paddingLeft: "20px", minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                        <h3 style={{ fontSize: "20px", marginBottom: "10px", marginTop: "10px" }}>
                          {study.category}
                        </h3>
                        <p>
                          <i className="fa fa-map-marker" style={{ marginRight: "8px", color: "#229626" }}></i>
                          {study.county}
                        </p>
                        <div>
                          {study.description
                            ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(study.description) }} />
                            : null}
                        </div>
                      </Grid>
                    </Grid>
                  )
                ) : isMobile ? (
                  <Grid container style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Grid item xs={12} style={{ paddingLeft: 0, paddingBottom: 16 }}>
                      <h3 style={{ fontSize: "20px", marginBottom: "10px", marginTop: "10px" }}>
                        {study.category}
                      </h3>
                      <p>
                        <i className="fa fa-map-marker" style={{ marginRight: "8px", color: "#229626" }}></i>
                        {study.county}
                      </p>
                      <div style={{ wordBreak: 'break-word', whiteSpace: 'normal', minWidth: 0 }}>
                        {study.description
                          ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(study.description) }} />
                          : null}
                      </div>
                    </Grid>
                    <Grid item xs={12} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={study.image}
                        alt={study.description}
                        style={{
                          maxHeight: "300px",
                          objectFit: "cover",
                          borderRadius: "2px",
                          width: '100%',
                          maxWidth: 300,
                          display: 'block',
                          marginBottom: '30px'
                        }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container style={{ display: "flex", overflow: "hidden", flexWrap: 'nowrap' }}>
                    <Grid item size={{ xs: 12, md: 4 }} style={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                      <img
                        src={study.image}
                        alt={study.description}
                        style={{
                          maxHeight: "300px",
                          objectFit: "cover",
                          marginRight: "20px",
                          borderRadius: "2px",
                          width: 300,
                          maxWidth: 300,
                          display: 'block',
                          marginBottom: 0
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12, md: 8 }} style={{ paddingLeft: "20px", minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                      <h3 style={{ fontSize: "20px", marginBottom: "10px", marginTop: "10px" }}>
                        {study.category}
                      </h3>
                      <p>
                        <i className="fa fa-map-marker" style={{ marginRight: "8px", color: "#229626" }}></i>
                        {study.county}
                      </p>
                      <div style={{ wordBreak: 'break-word', whiteSpace: 'normal', minWidth: 0 }}>
                        {study.description
                          ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(study.description) }} />
                          : null}
                      </div>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              {index < caseStudies.length - 1 && (
                <hr style={{ width: "100%", border: "none", borderTop: "1px solid #e0e0e0", margin: "20px 0" }} />
              )}
            </React.Fragment>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CaseStudies;
